/**
 * Socket.io Middleware for Redux
 * Handles Socket.io events and dispatches appropriate Redux actions
 */

import socketService from '../services/socketService';
import {
  WEBSOCKET_CONNECT,
  WEBSOCKET_DISCONNECT,
  WEBSOCKET_SEND_MESSAGE,
  WEBSOCKET_SEND_TYPING,
  WEBSOCKET_JOIN_CONVERSATION,
  WEBSOCKET_LEAVE_CONVERSATION,
  SEND_MESSAGE,
  RECEIVE_MESSAGE,
  TYPING_INDICATOR,
  USER_STATUS_CHANGE,
  READ_RECEIPT,
  MESSAGE_DELIVERED,
  MESSAGE_READ,
  UPDATE_MESSAGE,
  DELETE_MESSAGE,
  UPDATE_UNREAD_COUNT,
  WEBSOCKET_CONNECTION_STATUS,
} from '../actions/constants';

// Socket.io Middleware
const socketMiddleware = store => {
  // Keep track of event listeners to avoid duplicates
  let listenersInitialized = false;
  // Track joined conversations to prevent duplicate joins
  const joinedConversations = new Set();
  // Track the current user ID to detect changes
  let currentUserId = null;
  
  // Return the middleware function
  return next => action => {
    const { type, payload } = action;
    const { dispatch } = store;
    const currentUser = JSON.parse(localStorage.getItem('profile'))?.result;

    switch (type) {
      // Connect to Socket.io server
      case WEBSOCKET_CONNECT:
        // Check if the user ID has changed
        const userId = payload?.userId || currentUser?._id;
        
        // Only reconnect if the user ID has changed or we're not already connected
        if (userId !== currentUserId || !socketService.isConnected()) {
          // Disconnect existing connection if there is one
          if (socketService.isConnected()) {
            console.log('User changed, disconnecting existing socket');
            socketService.disconnect();
            listenersInitialized = false;
            joinedConversations.clear();
          }
          
          // Update current user ID
          currentUserId = userId;
          
          // Initialize the Socket.io connection
          console.log('Connecting socket for user:', userId);
          socketService.connect(payload?.token || currentUser?.token, userId);

          // Only set up event listeners once
          if (!listenersInitialized) {
            console.log('Setting up Socket.io event listeners');
            listenersInitialized = true;
            
            // Update connection status when socket connects
            socketService.onConnect(() => {
              console.log('Socket.io connected - dispatching status update');
              dispatch({
                type: WEBSOCKET_CONNECTION_STATUS,
                payload: { isConnected: true }
              });
              
              // Clear joined conversations on new connection
              joinedConversations.clear();
            });

            // Update connection status when socket disconnects
            socketService.onDisconnect(() => {
              console.log('Socket.io disconnected - dispatching status update');
              dispatch({
                type: WEBSOCKET_CONNECTION_STATUS,
                payload: { isConnected: false }
              });
              
              // Clear joined conversations on disconnect
              joinedConversations.clear();
            });

            // Set up event listeners for incoming messages
            socketService.onNewMessage(message => {
              console.log('New message received:', message.conversationId);
              dispatch({
                type: RECEIVE_MESSAGE,
                payload: {
                  conversationId: message.conversationId,
                  message
                }
              });
              
              // If message is not from current user, increment unread count
              if (message.sender._id !== currentUser?._id) {
                dispatch({
                  type: UPDATE_UNREAD_COUNT,
                  payload: {
                    conversationId: message.conversationId,
                    increment: true
                  }
                });
              }
            });

            // Listen for typing indicators
            socketService.onTypingIndicator(data => {
              dispatch({
                type: TYPING_INDICATOR,
                payload: {
                  conversationId: data.conversationId,
                  userId: data.userId,
                  isTyping: data.isTyping
                }
              });
            });

            // Listen for user status changes (online/offline)
            socketService.onUserStatusChange(data => {
              dispatch({
                type: USER_STATUS_CHANGE,
                payload: {
                  userId: data.userId,
                  status: data.status,
                  lastSeen: data.lastSeen
                }
              });
            });

            // Listen for read receipts
            socketService.onReadReceipt(data => {
              dispatch({
                type: READ_RECEIPT,
                payload: {
                  conversationId: data.conversationId,
                  userId: data.userId,
                  messageId: data.messageId,
                  timestamp: data.timestamp
                }
              });
            });

            // Listen for message delivered confirmations
            socketService.onMessageDelivered(data => {
              dispatch({
                type: MESSAGE_DELIVERED,
                payload: {
                  conversationId: data.conversationId,
                  messageId: data.messageId,
                  tempId: data.tempId
                }
              });
            });

            // Listen for message read confirmations
            socketService.onMessageRead(data => {
              dispatch({
                type: MESSAGE_READ,
                payload: {
                  conversationId: data.conversationId,
                  messageId: data.messageId
                }
              });
              
              // Reset unread count for this conversation
              dispatch({
                type: UPDATE_UNREAD_COUNT,
                payload: {
                  conversationId: data.conversationId,
                  increment: false
                }
              });
            });

            // Listen for message updates
            socketService.onMessageUpdated(data => {
              dispatch({
                type: UPDATE_MESSAGE,
                payload: {
                  conversationId: data.conversationId,
                  messageId: data.messageId,
                  message: data.message
                }
              });
            });

            // Listen for message deletions
            socketService.onMessageDeleted(data => {
              dispatch({
                type: DELETE_MESSAGE,
                payload: {
                  conversationId: data.conversationId,
                  messageId: data.messageId
                }
              });
            });
          }
        } else {
          console.log('Socket already connected for user:', currentUserId);
        }
        break;

      // Disconnect from Socket.io server
      case WEBSOCKET_DISCONNECT:
        console.log('Disconnecting Socket.io from middleware');
        socketService.disconnect();
        listenersInitialized = false;
        joinedConversations.clear();
        currentUserId = null;
        
        // Update connection status
        dispatch({
          type: WEBSOCKET_CONNECTION_STATUS,
          payload: { isConnected: false }
        });
        
        break;

      // Send a message via Socket.io
      case WEBSOCKET_SEND_MESSAGE:
        socketService.sendMessage({
          ...payload,
          userId: currentUser?._id,
          conversationId: payload.conversationId
        });
        break;

      // Send typing indicator via Socket.io
      case WEBSOCKET_SEND_TYPING:
        socketService.sendTypingIndicator({
          conversationId: payload.conversationId,
          userId: currentUser?._id,
          isTyping: payload.isTyping
        });
        break;

      // Join a conversation to receive updates
      case WEBSOCKET_JOIN_CONVERSATION:
        if (!payload.conversationId) {
          console.error('Cannot join conversation: No conversation ID provided');
          break;
        }
        
        // Only join if not already joined
        if (!joinedConversations.has(payload.conversationId)) {
          console.log(`Joining conversation: ${payload.conversationId}`);
          socketService.joinConversation(payload.conversationId);
          joinedConversations.add(payload.conversationId);
        } else {
          console.log(`Already joined conversation: ${payload.conversationId}`);
        }
        break;

      // Leave a conversation to stop receiving updates
      case WEBSOCKET_LEAVE_CONVERSATION:
        if (joinedConversations.has(payload.conversationId)) {
          socketService.leaveConversation(payload.conversationId);
          joinedConversations.delete(payload.conversationId);
        }
        break;

      // Handle message sent via API (update Socket.io status)
      case SEND_MESSAGE:
        // If this is a real-time message (from Socket.io), don't send it again
        if (!payload.isRealTime && socketService.isConnected()) {
          socketService.sendMessage({
            ...payload.message,
            userId: currentUser?._id,
            conversationId: payload.conversationId
          });
        }
        break;

      default:
        break;
    }

    // Pass the action to the next middleware or reducer
    return next(action);
  };
};

export default socketMiddleware;
